import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPlatformChromeTenant } from '../../../app/platformChromeTenant.js';
import { resolveTenantLegacyApiUrl } from '../../../shared/api/resolveApiUrl.js';
import { stormAwareFetch } from '../../../shared/http/stormAwareFetch.js';
import { resolvePublicApiFailureMessage } from '../../../shared/errors/publicApiFailureMessage.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';

export type ManualReconciliationCheckPhase = 'idle' | 'polling' | 'completing' | 'exhausted';

export type ManualReconciliationMessageKey =
  | 'verifying_first'
  | 'thank_you_still_looking'
  | 'refund_promise';

const DEFAULT_NEXT_POLL_SECONDS = 31;

/** R-2 — cap same-ordinal 429 reschedules so checkout cannot hammer forever. */
const MAX_COOLDOWN_RESCHEDULES = 3;

interface ManualCheckAttemptRequest {
  paymentId: string;
  checkoutSessionId: string;
  pollOrdinal: 1 | 2 | 3;
}

type ManualCheckResponseStatus = 'found' | 'not_found' | 'cap_reached' | 'exhausted';

interface ManualCheckResult {
  readonly status?: ManualCheckResponseStatus;
  readonly matchFound?: boolean;
  readonly messageKey?: ManualReconciliationMessageKey;
  readonly nextPollInSeconds?: number;
  readonly pollOrdinal?: 1 | 2 | 3;
}

interface ScheduledPoll {
  readonly ordinal: 1 | 2 | 3;
  readonly delayMs: number;
}

interface UseManualReconciliationCheckOptions {
  paymentId: string | null | undefined;
  checkoutSessionId: string | null | undefined;
  /** Row-tenant override when chrome is `__platform__` (account QR `?tenant=`). */
  tenantCode?: string | null;
}

interface UseManualReconciliationCheckResult {
  checkPhase: ManualReconciliationCheckPhase;
  messageKey: ManualReconciliationMessageKey | null;
  isCheckingNow: boolean;
  customerAssertedPaid: boolean;
  onAssertPaidClick: () => void;
}

function parseManualCheckStatus(result: ManualCheckResult | null): ManualCheckResponseStatus | undefined {
  const status = result?.status;
  if (status === 'found' || status === 'not_found') {
    return status;
  }
  if (status === 'cap_reached' || status === 'exhausted') {
    return 'cap_reached';
  }
  return undefined;
}

function parseMessageKey(
  value: string | undefined
): ManualReconciliationMessageKey | undefined {
  if (
    value === 'verifying_first' ||
    value === 'thank_you_still_looking' ||
    value === 'refund_promise'
  ) {
    return value;
  }
  return undefined;
}

function messageKeyForPollOrdinal(pollOrdinal: 1 | 2 | 3): ManualReconciliationMessageKey {
  if (pollOrdinal === 1) {
    return 'verifying_first';
  }
  if (pollOrdinal === 2) {
    return 'thank_you_still_looking';
  }
  return 'refund_promise';
}

/**
 * Mirror `resolvePaymentMonitoringTenant`: override ?? chrome, but never `__platform__`.
 * Returns null so assert-paid / polls are a no-op (safer UX than throwing on hub QR).
 */
function resolveManualCheckTenant(
  chromeTenantCode: string,
  overrideTenantCode: string | null | undefined
): string | null {
  const resolved =
    overrideTenantCode !== undefined &&
    overrideTenantCode !== null &&
    overrideTenantCode.trim().length > 0
      ? overrideTenantCode.trim()
      : chromeTenantCode.trim();
  if (resolved.length === 0 || isPlatformChromeTenant(resolved)) {
    return null;
  }
  return resolved;
}

async function postManualCheckAttempt(
  url: string,
  body: ManualCheckAttemptRequest
): Promise<ManualCheckResult> {
  const response = await stormAwareFetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const payload = (await response.json().catch(() => null)) as
    | { success?: boolean; data?: ManualCheckResult; error?: string }
    | null;
  if (response.status === 429) {
    const retryAfterHeader = response.headers.get('Retry-After');
    const retryAfterSecondsRaw = retryAfterHeader ? Number(retryAfterHeader) : NaN;
    const retryAfterSeconds =
      Number.isFinite(retryAfterSecondsRaw) && retryAfterSecondsRaw > 0
        ? retryAfterSecondsRaw
        : DEFAULT_NEXT_POLL_SECONDS;
    const err = new Error(
      resolvePublicApiFailureMessage({
        status: 429,
        rawMessage: payload?.error,
      }),
    );
    (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds = retryAfterSeconds;
    throw err;
  }
  if (!response.ok || payload?.success !== true || payload.data === undefined) {
    throw new Error(
      resolvePublicApiFailureMessage({
        status: response.status,
        rawMessage: payload?.error,
      }),
    );
  }
  return payload.data;
}

export function useManualReconciliationCheck({
  paymentId,
  checkoutSessionId,
  tenantCode: tenantCodeOverride,
}: UseManualReconciliationCheckOptions): UseManualReconciliationCheckResult {
  const { apiBaseUrl } = useRuntimeConfig();
  const chromeTenantCode = useTenantCode();
  const tenantCode = resolveManualCheckTenant(chromeTenantCode, tenantCodeOverride);
  const [checkPhase, setCheckPhase] = useState<ManualReconciliationCheckPhase>('idle');
  const [messageKey, setMessageKey] = useState<ManualReconciliationMessageKey | null>(null);
  const [isCheckingNow, setIsCheckingNow] = useState(false);
  const [scheduledPoll, setScheduledPoll] = useState<ScheduledPoll | null>(null);
  const checkPhaseRef = useRef(checkPhase);
  const cooldownRescheduleCountRef = useRef(0);

  useEffect(() => {
    checkPhaseRef.current = checkPhase;
  }, [checkPhase]);

  const manualCheckUrl = useMemo(() => {
    if (tenantCode === null) {
      return null;
    }
    return resolveTenantLegacyApiUrl(
      apiBaseUrl,
      tenantCode,
      '/payments/reconciliation/manual-check-attempt'
    );
  }, [apiBaseUrl, tenantCode]);

  const runPoll = useCallback(
    async (pollOrdinal: 1 | 2 | 3): Promise<void> => {
      const resolvedPaymentId = paymentId?.trim() ?? '';
      const resolvedCheckoutSessionId = checkoutSessionId?.trim() ?? '';
      if (
        manualCheckUrl === null ||
        resolvedPaymentId.length === 0 ||
        resolvedCheckoutSessionId.length === 0
      ) {
        return;
      }

      setIsCheckingNow(true);
      setCheckPhase('polling');
      setScheduledPoll(null);

      try {
        const result = await postManualCheckAttempt(manualCheckUrl, {
          paymentId: resolvedPaymentId,
          checkoutSessionId: resolvedCheckoutSessionId,
          pollOrdinal,
        });
        cooldownRescheduleCountRef.current = 0;
        const status = parseManualCheckStatus(result);
        const resolvedMessageKey =
          parseMessageKey(result.messageKey) ?? messageKeyForPollOrdinal(pollOrdinal);
        setMessageKey(resolvedMessageKey);

        if (result.matchFound === true || status === 'found') {
          setCheckPhase('completing');
          return;
        }

        if (status === 'cap_reached' || pollOrdinal === 3) {
          setCheckPhase('exhausted');
          setMessageKey('refund_promise');
          return;
        }

        const delaySeconds = result.nextPollInSeconds ?? DEFAULT_NEXT_POLL_SECONDS;
        const nextOrdinal = (pollOrdinal + 1) as 2 | 3;
        setScheduledPoll({
          ordinal: nextOrdinal,
          delayMs: delaySeconds * 1000,
        });
      } catch (err) {
        const retryAfterSeconds = (err as Error & { retryAfterSeconds?: number }).retryAfterSeconds;
        if (typeof retryAfterSeconds === 'number' && retryAfterSeconds > 0) {
          cooldownRescheduleCountRef.current += 1;
          if (cooldownRescheduleCountRef.current > MAX_COOLDOWN_RESCHEDULES) {
            setCheckPhase('exhausted');
            setMessageKey('refund_promise');
            return;
          }
          setScheduledPoll({
            ordinal: pollOrdinal,
            delayMs: retryAfterSeconds * 1000,
          });
          return;
        }
        if (pollOrdinal === 3) {
          setCheckPhase('exhausted');
          setMessageKey('refund_promise');
          return;
        }
        const nextOrdinal = (pollOrdinal + 1) as 2 | 3;
        setScheduledPoll({
          ordinal: nextOrdinal,
          delayMs: DEFAULT_NEXT_POLL_SECONDS * 1000,
        });
      } finally {
        setIsCheckingNow(false);
      }
    },
    [checkoutSessionId, manualCheckUrl, paymentId]
  );

  useEffect(() => {
    if (scheduledPoll === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      void runPoll(scheduledPoll.ordinal);
    }, scheduledPoll.delayMs);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [runPoll, scheduledPoll]);

  const onAssertPaidClick = useCallback((): void => {
    if (checkPhaseRef.current !== 'idle' || manualCheckUrl === null) {
      return;
    }
    cooldownRescheduleCountRef.current = 0;
    void runPoll(1);
  }, [manualCheckUrl, runPoll]);

  const customerAssertedPaid = checkPhase !== 'idle';

  return {
    checkPhase,
    messageKey,
    isCheckingNow,
    customerAssertedPaid,
    onAssertPaidClick,
  };
}
